import { useState } from 'react';
import { Loader2, Play, Building2, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { Button } from '../ui/button';
import { Badge } from '../ui/badge';
import { diagnosticsAPI } from '../../lib/api';
import { toast } from 'sonner';
import { getErrorMessage } from '../../lib/errors';

export function BridgeBankResolutionPanel() {
  const [loading, setLoading] = useState(false);
  const [result, setResult] = useState(null);

  const run = async () => {
    setLoading(true);
    setResult(null);
    try {
      const { data } = await diagnosticsAPI.bridgeBankResolution();
      setResult(data);
    } catch (err) {
      toast.error(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  };

  return (
    <Card>
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg flex items-center gap-2">
            <Building2 className="h-5 w-5 text-blue-600" />
            Bridge Bank Resolution
          </CardTitle>
          <Button onClick={run} disabled={loading} size="sm">
            {loading ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : (
              <Play className="h-4 w-4 mr-2" />
            )}
            Check
          </Button>
        </div>
        <p className="text-xs text-muted-foreground mt-0.5">
          Resolves each allowed bank against Bridge's live provider list. Verifies the bank can be found and uniquely matched before connect is attempted. Read-only.
        </p>
      </CardHeader>
      {result && (
        <CardContent className="space-y-4">
          {/* Overall banner */}
          <div className={`border rounded-lg p-3 ${result.pass ? 'bg-green-50 dark:bg-green-950/20 border-green-200 dark:border-green-800' : 'bg-red-50 dark:bg-red-950/20 border-red-200 dark:border-red-800'}`}>
            <div className="flex items-center gap-2 text-sm font-medium">
              {result.pass ? (
                <>
                  <CheckCircle2 className="h-4 w-4 text-green-600" />
                  <span className="text-green-700 dark:text-green-400">All allowed banks resolved ({result.banks?.length || 0} bank{result.banks?.length !== 1 ? 's' : ''})</span>
                </>
              ) : (
                <>
                  <AlertTriangle className="h-4 w-4 text-red-600" />
                  <span className="text-red-700 dark:text-red-400">
                    {result.error || 'Some banks could not be resolved'}
                  </span>
                </>
              )}
            </div>
            {result.total_providers != null && (
              <p className="text-xs text-muted-foreground mt-1">
                {result.total_providers} provider(s) in Bridge list{result.pages_walked != null ? ` (${result.pages_walked} page${result.pages_walked !== 1 ? 's' : ''} fetched)` : ''}
              </p>
            )}
          </div>

          {/* Per-bank results */}
          {result.banks && result.banks.length > 0 && (
            <div className="overflow-x-auto border rounded-lg">
              <table className="w-full text-xs">
                <thead>
                  <tr className="border-b bg-muted/50">
                    <th className="text-left px-3 py-1.5 font-medium">Allowed Name</th>
                    <th className="text-left px-3 py-1.5 font-medium">Status</th>
                    <th className="text-left px-3 py-1.5 font-medium">Resolved Provider</th>
                    <th className="text-left px-3 py-1.5 font-medium">Country</th>
                    <th className="text-left px-3 py-1.5 font-medium">Provider Status</th>
                  </tr>
                </thead>
                <tbody>
                  {result.banks.map((bank, i) => (
                    <tr key={i} className="border-b last:border-0">
                      <td className="px-3 py-1.5 font-mono font-medium">{bank.allowed_name}</td>
                      <td className="px-3 py-1.5">
                        {bank.pass ? (
                          <Badge className="bg-green-600 hover:bg-green-700 text-white gap-1 text-[10px]">
                            <CheckCircle2 className="h-3 w-3" />
                            Resolved
                          </Badge>
                        ) : (
                          <Badge variant="destructive" className="gap-1 text-[10px]">
                            <XCircle className="h-3 w-3" />
                            {bank.reason || 'Unresolved'}
                          </Badge>
                        )}
                      </td>
                      <td className="px-3 py-1.5 font-mono text-[10px]">
                        {bank.resolved ? (
                          <span>{bank.resolved.name} (id: {bank.resolved.provider_id})</span>
                        ) : bank.candidates && bank.candidates.length > 0 ? (
                          <span className="text-amber-600">
                            {bank.candidates.length} candidate(s): {bank.candidates.map(c => c.name).join(', ')}
                          </span>
                        ) : (
                          <span className="text-muted-foreground">--</span>
                        )}
                      </td>
                      <td className="px-3 py-1.5 text-[10px]">
                        {bank.resolved?.country || '--'}
                      </td>
                      <td className="px-3 py-1.5 text-[10px]">
                        {bank.resolved?.status || '--'}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}

          {result.checked_at && (
            <p className="text-[10px] text-muted-foreground text-right">
              Checked at {new Date(result.checked_at).toLocaleString()}
            </p>
          )}
        </CardContent>
      )}
    </Card>
  );
}
